import MirrorBackButton from "@/components/MirrorBackButton";
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, MotionConfig, useReducedMotion } from "framer-motion";
import { toast } from "sonner";
import { sfx } from "@/lib/sfx";
import MobileLayout from "@/components/MobileLayout";
import { Settings, ArrowRight, ChevronLeft, Star, Check } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { localeTagFor } from "@/i18n/locale";
import { InfoTooltip } from "@/components/InfoTooltip";
import { useAppActivity } from "@/contexts/AppActivityContext";
import { useFocus, type FocusSession } from "@/hooks/useFocus";
import { useXP } from "@/hooks/useXP";
import { useFocusTimer } from "@/hooks/useFocusTimer";
import { useFocusDictation } from "@/components/focus/useFocusDictation";
import { AlertDialog, AlertDialogContent, AlertDialogTitle, AlertDialogDescription, AlertDialogCancel } from "@/components/ui/alert-dialog";
import "./focus.css";
import { AnalyticsDashboard } from "@/components/analytics/AnalyticsDashboard";
import { buildDashboard } from "@/lib/analytics/dashboard";
import { FOCUS_DASHBOARD, extractFocusSamples } from "@/lib/analytics/focusDashboard";
import type { Granularity } from "@/lib/analytics/engine";
import FocusActionModal, { FocusPendingActionLauncher } from "@/components/mirror/FocusActionModal";
import { canonicalFocusRecord } from "../../supabase/functions/_shared/focusActionDomain";
import { newFocusOperationId, snapshotForFocusSession, type FocusActionPrefill, type FocusTimerSnapshot } from "@/lib/focusExecutor";
import {
  requestNotificationPermission,
  scheduleTimerEndNotification,
  cancelTimerEndNotification,
} from "@/lib/focus/notifications";
import { playTimerEndBeep, triggerTimerEndHaptics } from "@/lib/focus/feedback";
import { fetchCompletionVoiceUrl, unlockAudioElement } from "@/lib/focus/completionVoice";
import { getAudioAutoplay } from "@/hooks/useAudioAutoplay";
import {
  FOCUS_QUALITY_OPTIONS,
  cacheFocusReaction,
  interpolate,
  loadAdaptiveContext,
  pickSuggestion,
  type FocusQuality,
} from "@/lib/focus/tier3";

// ── Types ──────────────────────────────────────────────────────────────────


interface SessionOption {
  label: string;
  minutes: number;
  sublabel: string;
}

// ── Static data ────────────────────────────────────────────────────────────

const SESSION_OPTIONS: SessionOption[] = [
  { label: "25 min",  minutes: 25,  sublabel: "focus.mode.pomodoro"     },
  { label: "45 min",  minutes: 45,  sublabel: "focus.mode.deepWork"     },
  { label: "90 min",  minutes: 90,  sublabel: "focus.mode.flowState"    },
];

const FOCUS_TASK_KEYS = [
  "focus.task.deepWork",
  "focus.task.study",
  "focus.task.writing",
  "focus.task.code",
  "focus.task.creativity",
  "focus.task.review",
  "focus.task.planning",
];

// Mode-driven color system. Stays within VYV identity (cyan/purple/indigo all valid VYV palette tokens).
const MODE_COLORS = [
  { primary: "#00e5ff",          glow: "187 72% 50%", name: "cyan"   }, // 25min Pomodoro
  { primary: "hsl(265 60% 65%)", glow: "265 60% 55%", name: "purple" }, // 45min Deep Work
  { primary: "hsl(225 70% 60%)", glow: "225 70% 55%", name: "indigo" }, // 90min Flow State
];

const VYV_CARD = "linear-gradient(145deg, hsl(220 35% 13%), hsl(225 30% 10%))";
const VYV_BORDER = "1px solid hsl(220 50% 40% / 0.25)";

const LS_KEYS = {
  running: "vyv_focus_running",
  timeleft: "vyv_focus_timeleft",
  task: "vyv_focus_task",
  option: "vyv_focus_option",
  startedat: "vyv_focus_startedat",
  settingsNotifyOnEnd: "vyv_focus_settings_notifyOnEnd",
  settingsVibration: "vyv_focus_settings_vibration",
};

// Returns the persisted boolean preference, defaulting to true (Tier 1
// feedback opt-out, not opt-in). Storage value "false" parses to false;
// anything else (including missing key) parses to true.
function loadBoolSetting(key: string): boolean {
  try { return localStorage.getItem(key) !== "false"; } catch { return true; }
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60).toString().padStart(2, "0");
  const s = (seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

const TASK_EMOJIS: Record<string, string> = {
  "focus.task.deepWork": "\u{1F9E0}",
  "focus.task.study": "\u{1F4DA}",
  "focus.task.writing": "\u{270D}\uFE0F",
  "focus.task.code": "\u{1F4BB}",
  "focus.task.creativity": "\u{1F3A8}",
  "focus.task.review": "\u{1F50D}",
  "focus.task.planning": "\u{1F4CB}",
};

// ── Page ───────────────────────────────────────────────────────────────────

const FocusContent: React.FC<{ focus: ReturnType<typeof useFocus>; userId: string }> = ({ focus, userId }) => {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const { t, lang, tp } = useLanguage();
  const { startActivity, stopActivity } = useAppActivity();
  const stopActivityRef = useRef(stopActivity); stopActivityRef.current = stopActivity;
  const timer = useFocusTimer(userId), voice = useFocusDictation({ ownerId: userId, capture: focus.capture }), xp = useXP({ guarded: true });
  const mounted = useRef(false), ownsActivity = useRef(false);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(() => { mounted.current = true; const timeouts = timers.current; return () => { mounted.current = false; timeouts.forEach(clearTimeout); if (ownsActivity.current) stopActivityRef.current(); }; }, []);
  const later = (fn: () => void, ms: number) => { timers.current.push(setTimeout(() => { if (mounted.current) fn(); }, ms)); };
  const [focusGran, setFocusGran] = useState<Granularity>(FOCUS_DASHBOARD.defaultGranularity);
  const focusAnalytics = useMemo(() => buildDashboard(extractFocusSamples(focus.sessions), FOCUS_DASHBOARD, focusGran), [focus.sessions, focusGran]);
  const [optionChoice, setOptionChoice] = useState(0), [taskChoice, setTaskChoice] = useState(() => t("focus.task.deepWork"));
  const captureFocus = focus.capture, captureTimer = timer.capture;
  const selectedOption = timer.draft?.option ?? optionChoice, currentTask = timer.draft?.task ?? taskChoice;
  const setSelectedOption = (value: number) => { if (!timer.draft) setOptionChoice(value); };
  const setCurrentTask = (value: string) => { if (!timer.draft) setTaskChoice(value); };
  const timerState = timer.draft?.phase ?? "idle";
  const remaining = timer.draft ? timer.remaining : SESSION_OPTIONS[selectedOption].minutes * 60;
  const [showSettings, setShowSettings] = useState(false);
  const [notifyOnEnd, setNotifyOnEnd] = useState(() => loadBoolSetting(LS_KEYS.settingsNotifyOnEnd));
  const [vibration, setVibration] = useState(() => loadBoolSetting(LS_KEYS.settingsVibration));
  const [settingsError, setSettingsError] = useState(false);
  const [celebration, setCelebration] = useState<{ task: string; duration: number } | null>(null), [xpToast, setXpToast] = useState<string | null>(null);
  const [startFlash, setStartFlash] = useState(false), [completionError, setCompletionError] = useState(false), [completing, setCompleting] = useState(false);
  const [pendingQualitySession, setPendingQualitySession] = useState<FocusSession | null>(null);
  const pendingQualitySessionId = pendingQualitySession?.id ?? null, qualitySaving = false;
  const [actionPrefill, setActionPrefill] = useState<FocusActionPrefill | null>(null);
  const [adaptiveSuggestion, setAdaptiveSuggestion] = useState<string | null>(null), [adaptiveDismissed, setAdaptiveDismissed] = useState(false);
  const [stopSnapshot, setStopSnapshot] = useState<FocusTimerSnapshot | null>(null);
  const [confirmStop, setConfirmStop] = useState(false), [mutationError, setMutationError] = useState(false);
  const completeBusy = useRef(false);
  const [historyLimit, setHistoryLimit] = useState(20);
  const [historyPeriod, setHistoryPeriod] = useState<30 | 90 | null>(30);
  const historyRows = useMemo(() => { const cutoff = new Date(); cutoff.setHours(0, 0, 0, 0); cutoff.setDate(cutoff.getDate() - (historyPeriod ?? 0) + 1); return historyPeriod ? focus.sessions.filter(session => Date.parse(session.completed_at ?? "") >= cutoff.getTime()) : focus.sessions; }, [focus.sessions, historyPeriod]);
  const completionAudioRef = useRef<HTMLAudioElement | null>(null), completionVoiceReadyRef = useRef(false), permissionRequest = useRef<Promise<boolean> | null>(null);
  const notificationCopy = useMemo(() => ({ title: t("focus.notification.title"), body: t("focus.notification.body") }), [t]);
  useEffect(() => {
    const audio = new Audio(); completionAudioRef.current = audio;
    return () => { audio.pause(); audio.removeAttribute("src"); completionAudioRef.current = null; completionVoiceReadyRef.current = false; };
  }, []);
  const prepareVoice = async () => {
    if (!getAudioAutoplay() || completionVoiceReadyRef.current) return;
    const current = focus.capture(), audio = completionAudioRef.current; if (!audio) return;
    void unlockAudioElement(audio);
    const key = (Math.floor(Math.random() * 4) + 1) as 1 | 2 | 3 | 4;
    const url = await fetchCompletionVoiceUrl(key, lang, { ownerId: userId, isCurrent: () => mounted.current && current() });
    if (!mounted.current || !current() || !url) return; audio.src = url; audio.load(); completionVoiceReadyRef.current = true;
  };
  useEffect(() => {
    try { localStorage.setItem(LS_KEYS.settingsNotifyOnEnd, String(notifyOnEnd)); localStorage.setItem(LS_KEYS.settingsVibration, String(vibration)); setSettingsError(false); }
    catch { setSettingsError(true); }
  }, [notifyOnEnd, vibration]);
  // A scheduling response that arrives after pause/stop/unmount is cancelled.
  useEffect(() => {
    if (timer.draft?.phase !== "running") return;
    let active = true, notificationId: number | null = null;
    const current = captureFocus(), currentTimer = captureTimer(), isCurrent = () => active && mounted.current && current() && currentTimer();
    const deadline = timer.draft.deadline!;
    if (notifyOnEnd) void (async () => {
      if (!isCurrent()) return;
      const allowed = await (permissionRequest.current ??= requestNotificationPermission());
      if (!allowed || !isCurrent() || deadline <= Date.now()) return;
      const result = await scheduleTimerEndNotification(deadline - Date.now(), notificationCopy);
      if (!isCurrent() && result) await cancelTimerEndNotification(result.id); else notificationId = result?.id ?? null;
    })();
    return () => { active = false; if (notificationId !== null) void cancelTimerEndNotification(notificationId); };
  }, [timer.draft?.phase, timer.draft?.deadline, notifyOnEnd, notificationCopy, captureFocus, captureTimer]);
  useEffect(() => {
    if (timer.draft?.phase === "running") { ownsActivity.current = true; startActivity({ type: "focus", label: timer.draft.task, emoji: "🧠", meta: { mode: SESSION_OPTIONS[timer.draft.option].sublabel } }); }
    else if (ownsActivity.current) { ownsActivity.current = false; stopActivityRef.current(); }
  }, [timer.draft?.phase, timer.draft?.id, timer.draft?.task, timer.draft?.option, startActivity]);
  const refreshAdaptiveSuggestion = useCallback(async () => {
    const current = captureFocus(); const guard = { ownerId: userId, isCurrent: () => mounted.current && current() }; const ctx = await loadAdaptiveContext(userId, guard); if (!mounted.current || !current()) return;
    const pick = pickSuggestion(ctx); if (!pick.textKey || !ctx.lastSessionId) { setAdaptiveSuggestion(null); return; }
    const message = interpolate(t(pick.textKey), { minutes: pick.suggestedMinutes }); setAdaptiveSuggestion(message);
    void cacheFocusReaction({ userId, focusSessionId: ctx.lastSessionId, message, language: lang }, guard);
  }, [userId, captureFocus, t, lang]);
  useEffect(() => { void refreshAdaptiveSuggestion(); }, [refreshAdaptiveSuggestion]);
  const openHistoryAction = (session: FocusSession, action: "update" | "delete", patch: Partial<FocusActionPrefill> = {}) => {
    const expected = snapshotForFocusSession(session); if (!expected) { setMutationError(true); return; }
    const operation = newFocusOperationId(); setActionPrefill({ action, action_id: operation, operation_id: operation, focus_session_id: session.id, expected_user_id: userId, expected_snapshot: structuredClone(expected), ...patch });
  };
  const openSessionEdit = (session: FocusSession) => openHistoryAction(session, "update");
  const handleQualityTap = (quality: FocusQuality) => { if (pendingQualitySession) openHistoryAction(pendingQualitySession, "update", { focus_quality: quality }); };
  const handleQualitySkip = () => { setPendingQualitySession(null); setMutationError(false); };
  const openManualReport = (task = "", minutes = 25) => { const operation = newFocusOperationId(); setActionPrefill({ action: "create", action_id: operation, operation_id: operation, expected_user_id: userId, task, duration_minutes: minutes, source: "manual" }); };
  const sounded = useRef<string | null>(null);
  useEffect(() => {
    // Expiry feedback is independent of persistence; retrying a record is silent.
    if ((timer.remaining > 0 || !timer.draft) || sounded.current === timer.draft.id) return;
    sounded.current = timer.draft.id;
    // Do not replay yesterday's alarm when restoring an unsaved session.
    if (Date.now() - Date.parse(timer.draft.completedAt ?? new Date().toISOString()) > 60000) return;
    if (vibration) { const audio = completionAudioRef.current; if (getAudioAutoplay() && completionVoiceReadyRef.current && audio?.src) { audio.currentTime = 0; void audio.play().catch(() => playTimerEndBeep()); } else playTimerEndBeep(); void triggerTimerEndHaptics(); }
  }, [timer.draft, timer.remaining, vibration]);
  const saveCompleted = useCallback(async () => {
    const draft = timer.draft, snapshot = timer.snapshot && structuredClone(timer.snapshot); if (!draft || !snapshot || timer.remaining > 0 || completeBusy.current) return;
    const current = focus.capture(); completeBusy.current = true; setCompleting(true); setCompletionError(false);
    try {
      const response = await timer.finish(snapshot); if (!mounted.current || !current()) return;
      if (!response) { setCompletionError(true); return; }
      const saved = response.record ? canonicalFocusRecord({ session: response.record }, userId) : null;
      if (response.replayed !== true && saved) {
        setOptionChoice(draft.option); setTaskChoice(draft.task); setCelebration({ task: saved.task, duration: saved.duration_minutes });
        sfx.success("module"); toast.success(t("focus.sessionSaved"));
        later(() => { setCelebration(null); setPendingQualitySession(saved); }, 2500);
        const reward = response.xp;
        if (reward && typeof reward === "object" && "awards" in reward && Array.isArray(reward.awards)) {
          for (const award of reward.awards) { if (award.inserted === true) { const key = award.action === "flow_state" ? "focus.xp.flowState" : award.action === "daily_goal" ? "focus.xp.dailyGoal" : award.action === "streak_7" ? "focus.xp.streak7" : "focusAction.earnedXP"; setXpToast(t(key)); later(() => setXpToast(null), 2200); } }
        }
      }
      await xp.refresh({ ownerId: userId, isCurrent: () => mounted.current && current() });
    } catch { if (mounted.current && current()) { setCompletionError(true); sfx.error("module"); } }
    finally { completeBusy.current = false; if (mounted.current && current()) setCompleting(false); }
  }, [timer, focus, xp, t, userId]);
  const handleStart = async () => { if (voice.active || !focus.hasData || timer.busy) return; const current = focus.capture(); if (!await timer.start(currentTask, selectedOption) || !mounted.current || !current()) return; setStartFlash(true); later(() => setStartFlash(false), 600); void prepareVoice(); };
  const handlePause = () => { if (timer.snapshot) void timer.pause(structuredClone(timer.snapshot)); }, handleResume = () => { if (timer.snapshot) void timer.resume(structuredClone(timer.snapshot)); };
  const resumeAfterStop = useRef(false);
  const handleStop = async () => {
    if (timer.busy || !timer.snapshot) return; const snapshot = structuredClone(timer.snapshot), current = focus.capture(); resumeAfterStop.current = snapshot.timer.phase === "running";
    const paused = resumeAfterStop.current ? await timer.pause(snapshot) : null;
    if (!mounted.current || !current() || (resumeAfterStop.current && !paused?.timer)) return;
    setStopSnapshot({ timer: structuredClone(paused?.timer ?? snapshot.timer) }); setConfirmStop(true);
  };
  const dismissStop = async () => { if (timer.busy || !stopSnapshot) return; const snapshot = structuredClone(stopSnapshot), current = focus.capture();
    const shouldResume = resumeAfterStop.current && snapshot.timer.elapsed_ms < snapshot.timer.duration_minutes * 60000;
    if (shouldResume && !await timer.resume(snapshot)) return;
    if (mounted.current && current()) { resumeAfterStop.current = false; setConfirmStop(false); setStopSnapshot(null); }
  };
  const totalSeconds = SESSION_OPTIONS[selectedOption].minutes * 60;
  const progressPct = Math.max(0, Math.min(100, (totalSeconds - remaining) / totalSeconds * 100));

  // Mode-driven color (cyan / purple / indigo) follows the selected SESSION_OPTIONS index.
  const modeColor = MODE_COLORS[selectedOption];
  const arcColor = modeColor.primary;

  // Fixed timer geometry. Earlier resize on running caused dasharray jank;
  // immersive feel comes from background gradient + glow pulse, not size.
  const isRunning = timerState === "running";
  const TIMER_SIZE = 220;
  const TIMER_RADIUS = 90;
  const TIMER_STROKE = 8;
  const circumference = 2 * Math.PI * TIMER_RADIUS;
  const strokeDashoffset = circumference - (progressPct / 100) * circumference;

  const levelProgress = xp.getLevelProgress();

  return (
    <MobileLayout className="focus-page pb-28 pt-8 relative overflow-hidden">
      {/* ── Ambient Background — fullscreen mode-driven gradient when running ── */}
      <div
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          background: isRunning
            ? `radial-gradient(circle at center, hsl(${modeColor.glow} / 0.20) 0%, hsl(${modeColor.glow} / 0.06) 35%, #0a0a0f 70%)`
            : "linear-gradient(180deg, #0a0a0f 0%, #0d1117 100%)",
          transition: "background 0.6s ease",
        }}
      >
        <motion.div className="absolute rounded-full" style={{ width: 400, height: 400, top: "50%", left: "50%", transform: "translate(-50%, -50%)", background: "radial-gradient(circle, rgba(0,229,255,0.04) 0%, rgba(124,58,237,0.02) 50%, transparent 70%)" }} animate={reduceMotion ? undefined : ({ scale: [1, 1.15, 1], opacity: [0.5, 0.8, 0.5] })} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }} />
      </div>

      {/* ── XP Toast ── */}
      <AnimatePresence>
        {xpToast && (
          <motion.div
            key="xp-toast"
            initial={{ opacity: 0, y: -20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.9 }}
            className="fixed top-16 left-1/2 -translate-x-1/2 z-[60] px-5 py-2.5 rounded-xl font-display text-[13px] font-bold tracking-wide"
            style={{ background: `hsl(${modeColor.glow} / 0.20)`, border: `1px solid hsl(${modeColor.glow} / 0.50)`, color: modeColor.primary, boxShadow: `0 0 30px hsl(${modeColor.glow} / 0.25)`, backdropFilter: "blur(12px)" }}
          >
            {xpToast}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Celebration overlay ── */}
      <AnimatePresence>
        {celebration && (
          <motion.div
            key="celebration"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{ background: "rgba(5, 8, 16, 0.94)" }}
          >
            {/* Star burst particles */}
            {!reduceMotion && Array.from({ length: 10 }).map((_, i) => {
              const angle = (i / 10) * 360;
              const rad = (angle * Math.PI) / 180;
              return (
                <motion.div
                  key={`burst-${i}`}
                  className="absolute w-2 h-2 rounded-full"
                  style={{ left: "50%", top: "50%", background: i % 2 === 0 ? "#00e5ff" : "hsl(265 60% 65%)" }}
                  initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
                  animate={{ x: Math.cos(rad) * 120, y: Math.sin(rad) * 120, opacity: 0, scale: 0.3 }}
                  transition={{ duration: 1.2, delay: 0.2, ease: "easeOut" }}
                />
              );
            })}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="flex flex-col items-center gap-4 p-8 rounded-3xl"
              style={{ background: VYV_CARD, border: `2px solid hsl(${modeColor.glow} / 0.50)`, boxShadow: `0 0 80px hsl(${modeColor.glow} / 0.25)` }}
            >
              <motion.div
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{ background: "hsl(187 72% 50% / 0.15)", border: "2px solid #00e5ff", boxShadow: "0 0 30px hsl(187 72% 50% / 0.35)" }}
                animate={reduceMotion ? undefined : ({ scale: [1, 1.15, 1] })}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              >
                <Check size={32} style={{ color: "#00e5ff" }} />
              </motion.div>
              <p className="font-display text-[22px] font-bold text-white">{t("focus.sessionComplete")}</p>
              <p className="font-display text-[14px]" style={{ color: "#00e5ff", textShadow: "0 0 12px hsl(187 72% 50% / 0.40)" }}>
                {celebration.duration} min · {celebration.task}
              </p>

            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── BACK BUTTON ── */}
      <MirrorBackButton disabled={voice.active || completing || timer.busy} className="mb-4" />

      {/* ── HEADER ── */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex items-center justify-between mb-3 relative z-10"
      >
        <div>
          <p className="text-[9px] font-display tracking-[0.2em] uppercase mb-0.5"
            style={{ color: "hsl(187 72% 50% / 0.55)" }}>{t("focus.subtitle")}</p>
          <div className="flex items-center gap-2">
            <h1 className="font-display text-[26px] font-semibold text-foreground leading-none">{t("focus.title")}</h1>
            <InfoTooltip title={t("tooltips.focus.title")} description={t("tooltips.focus.description")} />
          </div>
        </div>
        <button
          aria-label={t("focus.settings")} aria-expanded={showSettings} onClick={() => setShowSettings(s => !s)}
          className="w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-300 active:scale-95"
          style={{
            background: showSettings ? "hsl(187 72% 50% / 0.20)" : "hsl(187 72% 50% / 0.10)",
            border: `1px solid hsl(187 72% 50% / ${showSettings ? "0.40" : "0.20"})`,
          }}
        >
          <Settings size={15} style={{ color: "hsl(187 72% 50%)" }} />
        </button>
      </motion.div>

      {/* ── XP BAR — hidden during running for the immersive takeover ── */}
      <AnimatePresence>
        {!isRunning && !xp.loading && (
          <motion.div
            key="xp-bar"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, height: 0, marginBottom: 0 }}
            transition={{ delay: 0.05, duration: 0.4 }}
            className="flex items-center gap-3 mb-5 relative z-10"
          >
            <span className="text-[10px] font-display font-bold px-2 py-0.5 rounded-full"
              style={{ background: "hsl(187 72% 50% / 0.15)", border: "1px solid hsl(187 72% 50% / 0.30)", color: "#00e5ff" }}>
              {t("focus.lvl")} {xp.level}
            </span>
            <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "hsl(220 35% 14%)" }}>
              <motion.div
                className="h-full rounded-full"
                style={{ background: "linear-gradient(90deg, #00e5ff, hsl(187 72% 65%))", boxShadow: "0 0 8px hsl(187 72% 50% / 0.40)" }}
                initial={{ width: 0 }}
                animate={{ width: `${levelProgress}%` }}
                transition={{ duration: 0.8 }}
              />
            </div>
            <span className="text-[10px] font-display" style={{ color: "hsl(218 14% 55%)" }}>
              {xp.totalXP} XP
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── DEEP WORK SCORE — hidden during running ── */}
      <AnimatePresence>
        {!isRunning && focus.todaySessions > 0 && (
          <motion.div
            key="deep-work-score"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, height: 0, marginBottom: 0 }}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="flex items-center gap-4 px-4 py-3 rounded-2xl mb-5 relative z-10"
            style={{ background: "linear-gradient(rgba(0,0,0,0.40), rgba(0,0,0,0.60)), url('https://lluvykhqgdnbfobjixbj.supabase.co/storage/v1/object/public/mirror-brief/room%20ex_012.jpeg') center/cover no-repeat", border: VYV_BORDER }}>
            <svg width="64" height="64" viewBox="0 0 64 64">
              <circle cx="32" cy="32" r="25" fill="none" stroke="hsl(218 14% 16%)" strokeWidth="5" />
              <circle cx="32" cy="32" r="25" fill="none" stroke="#00e5ff" strokeWidth="5"
                strokeLinecap="round" strokeDasharray={2 * Math.PI * 25} strokeDashoffset={2 * Math.PI * 25 * (1 - Math.min(focus.todayMinutes / 120, 1))}
                transform="rotate(-90 32 32)" style={{ transition: "stroke-dashoffset 0.8s ease", filter: "drop-shadow(0 0 4px hsl(187 72% 50% / 0.40))" }} />
              <text x="32" y="36" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold" fontFamily="'Sora', sans-serif">{focus.todayMinutes}</text>
            </svg>
            <div>
              <p className="text-[9px] font-display tracking-[0.18em] uppercase" style={{ color: "hsl(187 72% 50% / 0.55)" }}>{t("focus.deepWorkScore")}</p>
              <p className="font-display text-[13px] font-bold text-white">{focus.todaySessions} {tp("focus.session", focus.todaySessions)} · {focus.todayMinutes} {t("common.min")}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── TIER 3: ADAPTIVE MIRROR SUGGESTION (calm card, idle only) ── */}
      <AnimatePresence>
        {!isRunning && !pendingQualitySessionId && adaptiveSuggestion && !adaptiveDismissed && (
          <motion.div
            key="adaptive-suggestion"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, height: 0, marginBottom: 0 }}
            transition={{ duration: 0.35 }}
            className="rounded-2xl px-4 py-3 mb-4 relative z-10 flex items-start gap-3"
            style={{
              background: "linear-gradient(145deg, hsl(220 35% 12% / 0.85), hsl(225 30% 9% / 0.85))",
              border: "1px solid hsl(187 72% 50% / 0.30)",
              boxShadow: "0 0 18px hsl(187 72% 50% / 0.08), inset 0 1px 0 hsl(187 72% 50% / 0.06)",
            }}
          >
            <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
              style={{ background: "hsl(187 72% 50% / 0.14)", border: "1px solid hsl(187 72% 50% / 0.30)" }}>
              <Star size={12} style={{ color: "#00e5ff" }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-1"
                style={{ color: "hsl(187 72% 50% / 0.65)" }}>
                {t("focus.tier3.mirror.label")}
              </p>
              <p className="text-[12px] font-body leading-relaxed text-white/85">
                {adaptiveSuggestion}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setAdaptiveDismissed(true)}
              aria-label={t("common.close")}
              className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-foreground/40 hover:text-foreground/70 transition-colors"
            >
              <ChevronLeft size={12} className="rotate-90" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── TIER 3: POST-SESSION QUALITY PROMPT (inline, dismissible) ── */}
      <AnimatePresence>
        {pendingQualitySessionId && (
          <motion.div
            key="quality-prompt"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, height: 0, marginBottom: 0 }}
            transition={{ duration: 0.35 }}
            className="rounded-2xl px-4 py-4 mb-4 relative z-10"
            style={{
              background: "linear-gradient(145deg, hsl(220 35% 13%), hsl(225 30% 10%))",
              border: "1px solid hsl(187 72% 50% / 0.40)",
              boxShadow: "0 0 28px hsl(187 72% 50% / 0.12)",
            }}
          >
            <p className="font-display text-[14px] font-bold text-white mb-1">
              {t("focus.tier3.quality.title")}
            </p>
            <p className="text-[11px] font-body mb-3" style={{ color: "hsl(218 14% 60%)" }}>
              {t("focus.tier3.quality.sub")}
            </p>
            <div className="grid grid-cols-4 gap-2">
              {FOCUS_QUALITY_OPTIONS.map((q) => (
                <button
                  key={q.value}
                  type="button"
                  disabled={qualitySaving}
                  onClick={() => void handleQualityTap(q.value)}
                  className="rounded-xl py-2 px-1.5 flex flex-col items-center gap-1 transition-all active:scale-95 disabled:opacity-50"
                  style={{
                    background: "hsl(220 35% 16% / 0.6)",
                    border: "1px solid hsl(187 72% 50% / 0.25)",
                  }}
                >
                  <span className="text-[14px] leading-none">{q.emoji}</span>
                  <span className="font-display text-[10px] font-semibold" style={{ color: "hsl(218 14% 75%)" }}>
                    {t(q.labelKey)}
                  </span>
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={handleQualitySkip}
              disabled={qualitySaving}
              className="mt-3 mx-auto block text-[10px] font-display tracking-wide underline underline-offset-2 transition-colors disabled:opacity-50"
              style={{ color: "hsl(218 14% 50%)" }}
            >
              {t("focus.tier3.quality.skip")}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── SETTINGS PANEL ── */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ opacity: 0, y: -10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, y: -10, height: 0 }}
            transition={{ duration: 0.25 }}
            className="rounded-2xl p-4 mb-5 overflow-hidden relative z-10"
            style={{ background: VYV_CARD, border: VYV_BORDER }}
          >
            <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-4" style={{ color: "hsl(187 72% 50% / 0.65)" }}>{t("focus.settings")}</p>
            <div className="flex items-center justify-between mb-3">
              <p className="font-display text-[12px]" style={{ color: "hsl(218 14% 60%)" }}>{t("focus.notifyOnEnd")}</p>
              <button role="switch" aria-checked={notifyOnEnd} aria-label={t("focus.notifyOnEnd")} onClick={() => setNotifyOnEnd(v => !v)}
                className="w-10 h-6 rounded-full transition-all duration-200 relative"
                style={{ background: notifyOnEnd ? "hsl(187 72% 50% / 0.35)" : "hsl(218 14% 18%)", border: `1px solid ${notifyOnEnd ? "hsl(187 72% 50% / 0.50)" : "hsl(218 14% 25%)"}` }}>
                <motion.div className="w-4 h-4 rounded-full absolute top-0.5"
                  animate={{ left: notifyOnEnd ? 20 : 4 }}
                  transition={{ duration: 0.15 }}
                  style={{ background: notifyOnEnd ? "#00e5ff" : "hsl(218 14% 40%)" }} />
              </button>
            </div>
            <div className="flex items-center justify-between mb-4">
              <p className="font-display text-[12px]" style={{ color: "hsl(218 14% 60%)" }}>{t("focus.vibration")}</p>
              <button role="switch" aria-checked={vibration} aria-label={t("focus.vibration")} onClick={() => setVibration(v => !v)}
                className="w-10 h-6 rounded-full transition-all duration-200 relative"
                style={{ background: vibration ? "hsl(187 72% 50% / 0.35)" : "hsl(218 14% 18%)", border: `1px solid ${vibration ? "hsl(187 72% 50% / 0.50)" : "hsl(218 14% 25%)"}` }}>
                <motion.div className="w-4 h-4 rounded-full absolute top-0.5"
                  animate={{ left: vibration ? 20 : 4 }}
                  transition={{ duration: 0.15 }}
                  style={{ background: vibration ? "#00e5ff" : "hsl(218 14% 40%)" }} />
              </button>
            </div>
            <button onClick={() => { if (!settingsError) { setShowSettings(false); toast(t("focus.settingsSaved")); } }}
              className="w-full py-2.5 rounded-xl font-display text-[12px] font-semibold active:scale-95 transition-all"
              style={{ background: "#00e5ff", color: "#0a0a0f" }}>
              {t("common.save")}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {!focus.historyComplete && focus.hasData && <p className="focus-status">{t("focusAction.partial")}</p>}
      {focus.loadError && <section role="alert" className="focus-status"><p>{t(focus.hasData ? "focus.ux.stale" : "focus.ux.loadError")}</p><button disabled={focus.isRefreshing} onClick={() => void focus.refresh()}>{t("focus.ux.retry")}</button></section>}
      {(timer.storageError || settingsError) && <p role="alert" className="focus-status">{t("focus.ux.storageError")}</p>}
      {timer.draft && timer.remaining === 0 && <section role={completionError ? "alert" : "status"} className="focus-status"><p>{t(completionError ? "focus.ux.saveRetry" : "focusAction.finishReview")}</p><button disabled={completing || timer.busy} onClick={() => void saveCompleted()}>{t(completing ? "focus.ux.savingSession" : "focusAction.finish")}</button></section>}
      {timer.error && <p role="alert" className="focus-status">{t("focusAction.outcomeUnknown")}</p>}
      {timer.legacyDraft && <section className="focus-status"><p>{t("focusAction.legacyDraft")}</p><button onClick={() => openManualReport(timer.legacyDraft!.task, SESSION_OPTIONS[timer.legacyDraft!.option].minutes)}>{t("focusAction.reviewReport")}</button></section>}
      <p className="focus-status">{t("focusAction.timezone")}: {timer.timer?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone}</p>
      <FocusPendingActionLauncher ownerId={userId} busy={focus.saving || timer.busy || !!actionPrefill} onMirrorResume={locator => navigate("/mirror", { state: { focusActionRecovery: locator } })} onConfirmed={async () => { const current = captureFocus(); await xp.refresh({ ownerId: userId, isCurrent: () => mounted.current && current() }); }} />
      {mutationError && <p role="alert" className="focus-status">{t("focus.saveError")}</p>}

      {/* ── TIMER CIRCLE ── */}
      <motion.div
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.06, duration: 0.5 }}
        className="flex flex-col items-center mb-6 relative z-10"
      >
        {/* Radial glow behind timer */}
        <div className="absolute w-[300px] h-[300px] rounded-full pointer-events-none" style={{ background: `radial-gradient(circle, hsl(${modeColor.glow} / 0.05) 0%, transparent 70%)`, top: "50%", left: "50%", transform: "translate(-50%, -55%)" }} />
        <motion.div
          className="relative flex items-center justify-center mb-4 rounded-full"
          style={{ width: TIMER_SIZE, height: TIMER_SIZE }}
          animate={reduceMotion ? undefined : ({
            boxShadow: isRunning
              ? [`0 0 25px ${modeColor.primary}55`, `0 0 50px ${modeColor.primary}aa`, `0 0 25px ${modeColor.primary}55`]
              : "0 0 0px transparent",
          })}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          {/* Start flash */}
          <AnimatePresence>
            {startFlash && (
              <motion.div
                key="start-flash"
                className="absolute inset-0 rounded-full pointer-events-none"
                initial={{ opacity: 0.6, scale: 0.9 }}
                animate={{ opacity: 0, scale: 1.3 }}
                transition={{ duration: 0.6 }}
                style={{ border: `2px solid ${modeColor.primary}`, boxShadow: `0 0 40px ${modeColor.primary}99` }}
              />
            )}
          </AnimatePresence>
          {/* Background circle */}
          <svg className="absolute inset-0 -rotate-90" width={TIMER_SIZE} height={TIMER_SIZE}>
            <defs>
              <linearGradient id="timerGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={modeColor.primary}/>
                <stop offset="100%" stopColor={`hsl(${modeColor.glow})`}/>
              </linearGradient>
            </defs>
            <circle cx={TIMER_SIZE / 2} cy={TIMER_SIZE / 2} r={TIMER_RADIUS}
              fill="none" strokeWidth={TIMER_STROKE} style={{ stroke: `hsl(${modeColor.glow} / 0.10)` }} />
            <motion.circle cx={TIMER_SIZE / 2} cy={TIMER_SIZE / 2} r={TIMER_RADIUS}
              fill="none" strokeWidth={TIMER_STROKE} strokeLinecap="round"
              style={{ stroke: "url(#timerGradient)", filter: `drop-shadow(0 0 6px ${modeColor.primary}80)` }}
              strokeDasharray={circumference}
              animate={{ strokeDashoffset }}
              transition={{ duration: 0.5 }} />
          </svg>
          {/* Time text */}
          <div className="text-center z-10">
            <p className="font-display text-[48px] font-bold text-foreground leading-none">
              {formatTime(remaining)}
            </p>
            <p className="text-[14px] font-display font-semibold text-center mt-1.5"
              style={{ color: "white", textShadow: `0 0 12px ${modeColor.primary}80` }}>
              {currentTask}
            </p>
          </div>
        </motion.div>

        {/* State label */}
        <p className="font-display text-[12px] tracking-wide mb-5"
          style={{ color: isRunning ? modeColor.primary : "hsl(218 14% 50%)" }}>
          {timerState === "idle"    && t("focus.readyToFocus")}
          {timerState === "running" && t("focus.focusing")}
          {timerState === "paused"  && t("focus.paused")}
          {timerState === "finished" && t("focusAction.timeElapsed")}
        </p>

        {/* Controls */}
        <AnimatePresence mode="wait">
          {timerState === "idle" && (
            <motion.button key="start"
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
              onClick={handleStart} disabled={!focus.hasData || !timer.ready || timer.busy || voice.active || focus.saving}
              className="w-full max-w-[320px] mx-auto py-4 px-6 rounded-xl font-display text-[15px] font-bold tracking-wide transition-all duration-200 active:scale-95"
              style={{
                background: modeColor.primary,
                color: "hsl(var(--background))",
                boxShadow: `0 0 30px ${modeColor.primary}55, 0 4px 20px ${modeColor.primary}33`,
              }}>
              {t("focus.startSession")}
            </motion.button>
          )}
          {timerState === "running" && (
            <motion.div key="running" className="flex gap-3"
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}>
              <button disabled={timer.busy} onClick={handlePause}
                className="px-6 py-2.5 rounded-xl font-display text-[12px] font-semibold tracking-wide transition-all active:scale-95"
                style={{ background: "hsl(187 72% 50% / 0.12)", border: "1px solid hsl(187 72% 50% / 0.30)", color: "hsl(187 72% 60%)" }}>
                {t("focus.pause")}
              </button>
              <button disabled={timer.busy} onClick={handleStop}
                className="px-6 py-2.5 rounded-xl font-display text-[12px] font-semibold tracking-wide transition-all active:scale-95"
                style={{ background: "hsl(0 55% 40% / 0.15)", border: "1px solid hsl(0 55% 50% / 0.25)", color: "hsl(0 60% 60%)" }}>
                {t("focus.stop")}
              </button>
            </motion.div>
          )}
          {timerState === "paused" && (
            <motion.div key="paused" className="flex gap-3"
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}>
              <button disabled={timer.busy} onClick={handleResume}
                className="px-6 py-2.5 rounded-xl font-display text-[12px] font-bold tracking-wide transition-all active:scale-95"
                style={{ background: modeColor.primary, color: "hsl(var(--background))" }}>
                {t("focus.resume")}
              </button>
              <button disabled={timer.busy} onClick={handleStop}
                className="px-6 py-2.5 rounded-xl font-display text-[12px] font-semibold tracking-wide transition-all active:scale-95"
                style={{ background: "hsl(0 55% 40% / 0.15)", border: "1px solid hsl(0 55% 50% / 0.25)", color: "hsl(0 60% 60%)" }}>
                {t("focus.stop")}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* ── SESSION OPTIONS ── */}
      <AnimatePresence>
        {timerState !== "running" && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4 }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-3 gap-2 mb-5 relative z-10">
              {SESSION_OPTIONS.map((opt, i) => {
                const active = selectedOption === i;
                const optColor = MODE_COLORS[i];
                return (
                  <button key={opt.label}
                    disabled={timerState !== "idle" || voice.active} onClick={() => setSelectedOption(i)}
                    className="rounded-xl py-3 px-2 text-center transition-all duration-200 active:scale-95"
                    style={active
                      ? { background: optColor.primary, color: "hsl(var(--background))", boxShadow: `0 0 30px ${optColor.primary}66, 0 0 60px ${optColor.primary}22` }
                      : { background: VYV_CARD, border: VYV_BORDER, color: "hsl(218 14% 60%)" }
                    }
                  >
                    <p className="font-display text-[13px] font-bold">{opt.label}</p>
                    <p className="text-[9px] font-body mt-0.5" style={{ color: active ? "#0a0a1a99" : "hsl(218 14% 55%)" }}>
                      {t(opt.sublabel)}
                    </p>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── FOCUS TASK INPUT + CHIPS ── */}
      <AnimatePresence>
        {timerState !== "running" && (
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: "auto" }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.4 }}
        className="mb-5 relative z-10 overflow-hidden"
      >
        <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-2" style={{ color: "hsl(187 72% 50% / 0.55)" }}>
          {t("focus.taskInputLabel")}
        </p>
        <textarea
          rows={3}
          aria-label={t("focus.edit.task")} disabled={timerState !== "idle"} value={currentTask}
          onChange={(e) => setCurrentTask(e.target.value)}
          placeholder={t("focus.taskPlaceholder")}
          className="w-full px-4 py-3 rounded-xl resize-none font-display text-base leading-relaxed mb-3 focus:outline-none focus:border-[hsl(187_72%_50%/0.5)] transition-colors"
          style={{ background: VYV_CARD, border: VYV_BORDER, color: "white", fontFamily: "inherit" }}
        />
        <div className="flex gap-2 overflow-x-auto pb-1 [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}>
          {FOCUS_TASK_KEYS.map(taskKey => {
            const taskLabel = t(taskKey);
            const isActive = currentTask === taskLabel;
            const emoji = TASK_EMOJIS[taskKey] || "";
            return (
              <button key={taskKey} disabled={timerState !== "idle"} onClick={() => setCurrentTask(taskLabel)}
                className="shrink-0 whitespace-nowrap px-3 py-2 rounded-full font-display text-[11px] tracking-wide transition-all active:scale-95"
                style={isActive
                  ? { background: modeColor.primary, color: "#0a0a0f", fontWeight: 600, boxShadow: `0 0 12px hsl(${modeColor.glow} / 0.30)` }
                  : { border: `1px solid hsl(${modeColor.glow} / 0.35)`, color: `hsl(${modeColor.glow})`, background: "transparent" }
                }>
                {emoji && <span className="mr-1">{emoji}</span>}{taskLabel}
              </button>
            );
          })}
        </div>
      </motion.div>
        )}
      </AnimatePresence>

      {timerState === "idle" && <div className="relative z-10">{voice.button(t("focus.edit.task"), text => setTaskChoice(previous => [previous.trim(), text].filter(Boolean).join(" ")))}</div>}

      {/* ── QUICK STATS ── */}
      <AnimatePresence>
        {timerState !== "running" && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4 }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-3 gap-2 mb-5 relative z-10">
              {[
                { label: t("focus.today"),   value: `${focus.todaySessions}` },
                { label: t("focus.thisWeek"), value: `${focus.weeklyMinutes} min` },
                { label: t("focus.streak"),  value: `${focus.streak} ${t("focus.days")}` },
              ].map((s) => (
                <div key={s.label} className="rounded-xl p-3 text-center"
                  style={{ background: VYV_CARD, border: VYV_BORDER, borderTop: "1px solid hsl(187 72% 50% / 0.25)" }}>
                  <p className="font-display text-[13px] font-bold text-foreground">{s.value}</p>
                  <p className="text-[9px] font-body mt-0.5" style={{ color: "hsl(218 14% 55%)" }}>{s.label}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── EVOLUÇÃO — shared analytics dashboard (day/week/month + trend +
          projection). Descriptive only (no productivity advice). ── */}
      <AnimatePresence>
        {timerState !== "running" && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4 }}
            className="mb-5 relative z-10 overflow-hidden"
          >
            <p className="text-[11px] font-display tracking-[0.18em] uppercase mb-3"
              style={{ color: "hsl(187 72% 50% / 0.55)" }}>{t("focus.analytics.title")}</p>
            <AnalyticsDashboard
              results={focusAnalytics}
              granularity={focusGran}
              onGranularityChange={setFocusGran}
              emptyKey="focus.analytics.empty"
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── SESSION HISTORY (chronological, edit + delete-with-undo) ── */}
      <AnimatePresence>
        {timerState !== "running" && (
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: "auto" }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.4 }}
        className="mb-5 relative z-10 overflow-hidden"
      >
        <p className="text-[11px] font-display tracking-[0.18em] uppercase mb-3"
          style={{ color: "hsl(187 72% 50% / 0.55)" }}>{t("focus.recentSessions")}</p>
        <div className="flex gap-2 mb-3">{([30, 90, null] as const).map(days => <button key={String(days)} className="focus-secondary" aria-pressed={historyPeriod === days} onClick={() => { setHistoryPeriod(days); setHistoryLimit(20); }}>{t(days === 30 ? "moduleHistory.period30" : days === 90 ? "moduleHistory.period90" : "moduleHistory.periodAll")}</button>)}</div>
        <button className="focus-secondary mb-3" onClick={() => openManualReport()}>{t("focusAction.reportSession")}</button>
        <div className="space-y-3">
          {!historyRows.length && <p className="text-sm text-muted-foreground">{t("focus.emptyState")}</p>}
          {historyRows.slice(0, historyLimit).map(session => <article key={session.id} className="focus-history-row">
            <h3>{session.task}</h3><p>{session.completed_at ? new Date(session.completed_at).toLocaleString(localeTagFor(lang), { dateStyle: "medium", timeStyle: "short" }) : t("focusAction.none")} · {session.duration_minutes} {t("common.min")}</p>
            <div className="flex gap-3"><button disabled={focus.saving || !session.writable} onClick={() => openSessionEdit(session)} aria-label={`${t("focus.ux.edit")}: ${session.task}`}>{t("focus.ux.edit")}</button><button disabled={focus.saving || !session.writable} onClick={() => openHistoryAction(session, "delete")} aria-label={`${t("common.delete")}: ${session.task}`}>{t("common.delete")}</button></div>
          </article>)}
          {historyRows.length > historyLimit && <button className="focus-secondary" onClick={() => setHistoryLimit(value => value + 20)}>{t("focus.ux.showMore")}</button>}
        </div>

        {/* ── MIRROR BRIEF ── */}
        <div className="rounded-2xl p-4 mb-5 relative overflow-hidden z-10"
          style={{
            background: "linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.65)), url(https://lluvykhqgdnbfobjixbj.supabase.co/storage/v1/object/public/mirror-brief/room%20ex_015.jpeg) center/cover no-repeat",
          border: "1.5px solid hsl(187 72% 50% / 0.35)",
          boxShadow: "0 0 20px hsl(187 72% 50% / 0.07)",
        }}>
        {/* Rotating gradient border top */}
        <div className="absolute top-0 left-0 right-0 h-[2px] overflow-hidden">
          <motion.div
            className="h-full w-[200%]"
            style={{ background: "linear-gradient(90deg, transparent, #00e5ff, hsl(265 60% 65%), #00e5ff, transparent)" }}
            animate={reduceMotion ? undefined : ({ x: ["-50%", "0%"] })}
            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          />
        </div>
        <div className="flex items-start gap-3">
          <motion.div
            className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: "hsl(187 72% 50% / 0.14)", border: "1px solid hsl(187 72% 50% / 0.25)" }}
            animate={reduceMotion ? undefined : ({ scale: [1, 1.08, 1] })}
            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
          >
            <Star size={14} style={{ color: "#00e5ff" }} />
          </motion.div>
          <div className="flex-1 min-w-0">
            <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-1.5" style={{ color: "hsl(187 72% 50% / 0.65)" }}>{t("focus.mirrorBrief")}</p>
            <p className="text-[12px] font-body text-foreground/65 leading-relaxed italic mb-3">
              {t("focus.mirrorBriefText")}
            </p>
            <button onClick={() => navigate("/mirror")}
              className="flex items-center gap-1 font-display text-[11px] font-medium tracking-wide opacity-80 hover:opacity-100 transition-opacity"
              style={{ color: "#00e5ff" }}>
              <span>{t("focus.planWithMirror")}</span>
              <ArrowRight size={11} />
            </button>
          </div>
        </div>
      </div>
      </motion.div>
        )}
      </AnimatePresence>

      <FocusActionModal open={!!actionPrefill} prefill={actionPrefill} onClose={() => setActionPrefill(null)} onCreated={() => undefined} onConfirmed={async () => { setPendingQualitySession(null); setAdaptiveDismissed(false); await focus.refresh(); }} />
      <AlertDialog open={confirmStop} onOpenChange={open => { if (!open) void dismissStop(); }}>
        <AlertDialogContent><AlertDialogTitle>{t("focus.ux.stopConfirm")}</AlertDialogTitle><AlertDialogDescription>{stopSnapshot?.timer.task} · {t("focus.ux.stopHint")}</AlertDialogDescription>
          <div className="flex gap-3"><AlertDialogCancel disabled={timer.busy}>{t("common.cancel")}</AlertDialogCancel><button disabled={timer.busy} className="focus-secondary" onClick={() => { const current = focus.capture(); if (!stopSnapshot) return; const snapshot = structuredClone(stopSnapshot); void timer.reset(snapshot.timer.id, snapshot).then(ok => { if (ok && mounted.current && current()) { resumeAfterStop.current = false; setConfirmStop(false); setStopSnapshot(null); } }); }}>{t("common.confirm")}</button></div>
        </AlertDialogContent>
      </AlertDialog>
    </MobileLayout>
  );
};

const Focus: React.FC = () => {
  const focus = useFocus(), { t } = useLanguage();
  if (!focus.userId || !focus.hasData) return <MobileLayout className="focus-page pt-8 pb-28"><MirrorBackButton /><h1>{t("focus.title")}</h1><section role={focus.loadError ? "alert" : "status"} className="focus-status"><p>{t(focus.status === "signed-out" ? "focus.ux.signIn" : focus.loadError ? "focus.ux.loadError" : "focus.ux.loading")}</p>{focus.loadError && <button disabled={focus.isRefreshing} onClick={() => void focus.refresh()}>{t("focus.ux.retry")}</button>}</section></MobileLayout>;
  return <MotionConfig reducedMotion="user"><FocusContent key={focus.userId + ":" + focus.identityGeneration} focus={focus} userId={focus.userId} /></MotionConfig>;
};
export default Focus;
